import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ExternalLink, FileText, Scale, ChevronDown, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { JurisprudenceCardList } from '@/components/case/jurisprudence/JurisprudenceCardList';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import type { ParsedJurisprudence } from '@/utils/jurisprudenceParser';
import { JURISDICTION_COLORS, JURISDICTION_LABELS, formatChamber } from './types';
import type { JurisprudenceSource, FondementJuridique } from './types';
import { Scroll } from 'lucide-react';
import { DecisionViewerService } from '@/services/decisionViewerService';

interface JurisprudenceSearchResultsProps {
  jobId: string;
  /**
   * Dossier de la conversation, reporté à l'ouverture d'une décision.
   *
   * WHY : le panneau de décision présélectionne ce dossier dans « Enregistrer ».
   * Il le déduit de l'URL quand on est dans un dossier, mais l'assistant global
   * (`/assistant`) n'en désigne aucune — sans ce report, l'avocat devait rechoisir
   * son dossier alors que la conversation en porte un.
   */
  caseId?: string;
  responseText: string;
  jurisprudences: ParsedJurisprudence[];
  sources: JurisprudenceSource[];
  fondements: FondementJuridique[];
}

type Tab = 'response' | 'sources' | 'fondements';

export function JurisprudenceSearchResults({
  jobId,
  caseId,
  responseText,
  jurisprudences,
  sources,
  fondements,
}: JurisprudenceSearchResultsProps) {
  // Collapsed par défaut : les fiches d'arrêts restent accessibles d'un clic,
  // mais c'est la réponse finale de l'agent dans la conversation qui porte la
  // synthèse juridique. (issue #289)
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('response');

  /** Sort sources: highest pertinence first, then most recent date */
  const sortedSources = useMemo(() =>
    [...sources].sort((a, b) => {
      const scoreDiff = b.pertinence_score - a.pertinence_score;
      if (scoreDiff !== 0) return scoreDiff;
      return (b.date ?? '').localeCompare(a.date ?? '');
    }),
    [sources],
  );

  /**
   * Set of source ids cited by the final response — a source is "cited" when it
   * matches (by reference prefix) one of the top merged JP cards shown under the
   * synthesis. Drives the star highlight in the Sources tab.
   */
  const citedSourceIds = useMemo(() => {
    const cited = new Set<string>();
    for (const jp of jurisprudences) {
      const jpRef = jp.reference?.trim();
      if (!jpRef) continue;
      for (const src of sources) {
        if (src.reference?.startsWith(jpRef) || jpRef.startsWith(src.reference)) {
          cited.add(src.id);
        }
      }
    }
    return cited;
  }, [jurisprudences, sources]);

  return (
    <div className="rounded-lg border border-grey-200 bg-white overflow-hidden">
      {/* Completed indicator — clickable, chevron collapse */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 border-b border-grey-100 bg-grey-50/60 cursor-pointer"
        onClick={() => setIsOpen(o => !o)}
      >
        <CheckCircle2 className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
        <Scale className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
        <span className="text-xs font-medium text-grey-600">Recherche de jurisprudence</span>
        {sources.length > 0 && (
          <span className="ml-1 px-1.5 py-0.5 bg-grey-100 text-grey-600 rounded text-xs font-medium">
            {sources.length} source{sources.length > 1 ? 's' : ''}
          </span>
        )}
        <span className="text-[11px] italic text-grey-400 whitespace-nowrap">
          ({isOpen ? 'Cliquer ici pour réduire' : 'Cliquer ici pour dérouler'})
        </span>
        <ChevronDown
          className={cn(
            'ml-auto w-3 h-3 text-grey-600 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            {/* Tab bar */}
            <div className="px-5 border-b border-grey-100 flex items-center">
              <TabButton
                active={activeTab === 'response'}
                onClick={() => setActiveTab('response')}
                label="Réponse"
                layoutId={`tab-underline-${jobId}`}
              />
              <TabButton
                active={activeTab === 'sources'}
                onClick={() => setActiveTab('sources')}
                label={`Sources (${sources.length})`}
                layoutId={`tab-underline-${jobId}`}
              />
              {fondements.length > 0 && (
                <TabButton
                  active={activeTab === 'fondements'}
                  onClick={() => setActiveTab('fondements')}
                  label={`Fondements (${fondements.length})`}
                  layoutId={`tab-underline-${jobId}`}
                />
              )}
            </div>

            {/* Content */}
            <AnimatePresence mode="wait" initial={false}>
              {activeTab === 'response' && (
                <motion.div
                  key="response"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="p-5 space-y-5"
                >
                  <MarkdownRenderer
                    content={responseText}
                    className="max-w-none text-xs text-grey-800 leading-relaxed text-justify"
                  />
                  {jurisprudences.length > 0 && (
                    <JurisprudenceCardList jurisprudences={jurisprudences} />
                  )}
                </motion.div>
              )}
              {activeTab === 'sources' && (
                <motion.div
                  key="sources"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="divide-y divide-grey-50"
                >
                  {sortedSources.length === 0 ? (
                    <p className="px-5 py-8 text-center text-sm text-grey-400">
                      Aucune source trouvée
                    </p>
                  ) : (
                    <>
                      {/* Column header */}
                      <div className="flex items-center py-2 px-5 gap-2 border-b border-grey-100 bg-grey-50/40">
                        <span className="w-3" />
                        <Star className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
                        <span className="flex-1 min-w-0 text-xs font-semibold text-grey-400 uppercase tracking-wider">Décision</span>
                        <span className="flex-shrink-0 w-28 text-center text-xs font-semibold text-grey-400 uppercase tracking-wider">N° pourvoi</span>
                        <span className="flex-shrink-0 w-44 text-center text-xs font-semibold text-grey-400 uppercase tracking-wider">Chambre</span>
                        <span className="flex-shrink-0 w-28 text-center text-xs font-semibold text-grey-400 uppercase tracking-wider">Lien</span>
                        <span className="flex-shrink-0 w-14 text-center text-xs font-semibold text-grey-400 uppercase tracking-wider">Juridiction</span>
                        <span className="flex-shrink-0 w-28 text-center text-xs font-semibold text-grey-400 uppercase tracking-wider">Pertinence</span>
                      </div>
                      {sortedSources.map(source => (
                        <SourceRow
                          key={source.id}
                          source={source}
                          isCited={citedSourceIds.has(source.id)}
                          caseId={caseId}
                        />
                      ))}
                    </>
                  )}
                </motion.div>
              )}
              {activeTab === 'fondements' && (
                <motion.div
                  key="fondements"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="p-5 space-y-3"
                >
                  {fondements.map((f, idx) => (
                    <FondementCard key={f.key || idx} fondement={f} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  label,
  layoutId,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  layoutId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative px-4 py-3.5 text-sm font-medium transition-colors duration-150',
        active ? 'text-grey-900' : 'text-grey-400 hover:text-grey-600',
      )}
    >
      {label}
      {active && (
        <motion.div
          layoutId={layoutId}
          className="absolute bottom-0 left-0 right-0 h-[2px] bg-grey-900 rounded-t"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.3 }}
        />
      )}
    </button>
  );
}

function getScoreStyle(score: number): { text: string; bar: string } {
  if (score >= 75) return { text: 'text-emerald-600', bar: 'bg-emerald-500' };
  if (score >= 50) return { text: 'text-amber-600', bar: 'bg-amber-500' };
  return { text: 'text-red-500', bar: 'bg-red-400' };
}

/** Format ISO date to readable French: "2023-10-12" → "12 oct. 2023" */
function formatDateFr(iso: string): string {
  const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juill.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
}

/** Extract pourvoi number from reference (strip ECLI part) : "21-17.453 (ECLI:...)" → "21-17.453" */
/** Extract pourvoi/RG number from reference — supports CC format (21-17.453) and CA format (22/00906) */
function extractPourvoi(reference: string): string {
  const match = reference.match(/^[\d][\d\-./\s]+/);
  return match ? match[0].trim() : reference;
}

function SourceRow({ source, isCited, caseId }: { source: JurisprudenceSource; isCited: boolean; caseId?: string }) {
  const [expanded, setExpanded] = useState(false);
  /** Backend sends 0–1 float, UI displays 0–100 integer */
  const score100 = source.pertinence_score <= 1
    ? Math.round(source.pertinence_score * 100)
    : Math.round(source.pertinence_score);
  const { text: scoreText, bar: scoreBar } = getScoreStyle(score100);

  const jurisdictionLabel = JURISDICTION_LABELS[source.jurisdiction] ?? source.jurisdiction;
  const pourvoi = extractPourvoi(source.reference);
  const dateFr = source.date ? formatDateFr(source.date) : null;
  const chamberLabel = formatChamber(source.chamber);

  /** Use LLM-formatted title if available, fallback to jurisdiction + date */
  const displayTitle = source.title || [jurisdictionLabel, dateFr].filter(Boolean).join(' · ');

  return (
    <div
      className={cn(
        'group px-5 transition-colors cursor-pointer',
        expanded ? 'bg-grey-50/80' : 'hover:bg-grey-50/80',
      )}
      onClick={() => setExpanded(o => !o)}
    >
      {/* Main row — fixed-width columns for alignment across rows */}
      <div className="flex items-center py-3 gap-2">
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-grey-600 flex-shrink-0 transition-transform duration-200 group-hover:text-grey-800',
            expanded && 'rotate-180',
          )}
        />

        {/* Star — highlighted if cited in the final response */}
        <Star
          className={cn(
            'w-3.5 h-3.5 flex-shrink-0',
            isCited ? 'text-amber-400 fill-amber-400' : 'text-grey-200',
          )}
        />

        {/* Title (jurisdiction + date) */}
        <span className="text-sm font-medium text-grey-800 truncate flex-1 min-w-0 group-hover:underline group-hover:decoration-grey-200 group-hover:underline-offset-2">
          {displayTitle}
        </span>

        {/* Pourvoi number */}
        <span className="flex-shrink-0 w-28 text-xs font-mono text-grey-600 text-center truncate" title={pourvoi}>
          n°{pourvoi}
        </span>

        {/* Chamber */}
        <span className="flex-shrink-0 w-44 text-center">
          {chamberLabel ? (
            <span className="px-2 py-0.5 rounded text-xs font-medium border border-grey-200 bg-grey-50 text-grey-600 whitespace-nowrap">
              {chamberLabel}
            </span>
          ) : (
            <span className="text-xs text-grey-400">Non renseignée</span>
          )}
        </span>

        {/* Judilibre link */}
        <span className="flex-shrink-0 w-28 text-center">
          {source.judilibre_url ? (
            <a
              href={source.judilibre_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              <span>Lien vers l'arrêt</span>
            </a>
          ) : source.ordre === 'administratif' && source.id ? (
            // Décision administrative : pas d'URL publique → viewer interne (full_text en base).
            // WHY: on branche sur `ordre` (source autoritaire, estampillé depuis Step1MetaRow)
            // et non sur `jurisdiction` (dérivée de `court` potentiellement défauté). (D7, 2026-06-18)
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                // `caseId` pré-sélectionne la destination dans « Enregistrer » ;
                // `displayTitle` nomme l'onglet tout de suite plutôt que « Arrêt… ».
                DecisionViewerService.viewDecision({
                  ordre: 'administratif',
                  id: source.id,
                  caseId,
                  title: displayTitle,
                });
              }}
              className="inline-flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue transition-colors"
            >
              <FileText className="w-3 h-3" />
              <span>Lire l'arrêt</span>
            </button>
          ) : (
            <span className="text-xs text-grey-400">Aucun lien</span>
          )}
        </span>

        {/* Jurisdiction badge */}
        <span className="flex-shrink-0 w-14 text-center">
          <span
            className={cn(
              'px-2 py-0.5 rounded text-xs font-bold border',
              JURISDICTION_COLORS[source.jurisdiction],
            )}
          >
            {source.jurisdiction}
          </span>
        </span>

        {/* Pertinence score */}
        <div className="flex-shrink-0 w-28 flex items-center gap-2">
          <div className="w-14 h-1.5 bg-grey-100 rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full', scoreBar)}
              style={{ width: `${score100}%` }}
            />
          </div>
          <span className={cn('text-xs font-mono font-semibold tabular-nums w-7 text-right', scoreText)}>
            {score100}
          </span>
        </div>
      </div>

      {/* Expanded — résumé officiel Judilibre uniquement */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pb-3 pl-6 pr-2 space-y-3">
              {/* Résumé Judilibre */}
              <div>
                <span className="text-xs font-semibold text-grey-400 uppercase tracking-wider">Résumé Judilibre</span>
                {source.official_summary ? (
                  <p className="mt-1 text-xs text-grey-600 leading-relaxed text-justify">{source.official_summary}</p>
                ) : (
                  <p className="mt-1 text-xs text-grey-400 italic">Aucun résumé disponible</p>
                )}
              </div>

              {/* Analyse de l'arrêt — raisonnement Mistral sur la pertinence */}
              {source.reasoning && (
                <div>
                  <span className="text-xs font-semibold text-grey-400 uppercase tracking-wider">Analyse de l'arrêt</span>
                  <p className="mt-1 text-xs text-grey-600 leading-relaxed text-justify">{source.reasoning}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FondementCard({ fondement }: { fondement: FondementJuridique }) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = fondement.content && fondement.content.trim().length > 0;

  return (
    <div className="rounded-lg border border-grey-200 overflow-hidden">
      <button
        type="button"
        className="flex items-center gap-3 w-full px-4 py-2.5 bg-grey-50/60 text-left hover:bg-grey-100/60 transition-colors"
        onClick={() => hasContent && setExpanded(o => !o)}
      >
        <Scroll className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-grey-800">{fondement.codeName}</span>
        <span className="text-xs font-mono text-grey-600">Art. {fondement.articleNumber}</span>
        {hasContent && (
          <ChevronDown
            className={cn(
              'w-3 h-3 text-grey-400 flex-shrink-0 ml-auto transition-transform duration-200',
              expanded && 'rotate-180',
            )}
          />
        )}
      </button>
      <AnimatePresence initial={false}>
        {expanded && hasContent && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 border-t border-grey-100">
              <p className="text-xs text-grey-600 leading-relaxed text-justify whitespace-pre-line">
                {fondement.content}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
